import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import OfficialDiaryAppService from '../../../../application/platform/sdp/official-diary/official-diary-app-service';
import BaseAppService from '../../../../application/platform/sdp/base/base-app-service';
import SubmissionPublicationAppService from '../../../../application/platform/sep/submission-publication/submission-publication-app-service';
import { IPreviewPublicationRequest } from '../../../../model/sdp/official-daily/request/preview-publication-request';
import { IConfirmPublicationRequest } from '../../../../model/sdp/official-daily/request/confirm-publication-request';
import { useBaseLayout } from '../../../../components/context/use-base-layout';
import { DefaultDialogProps } from '../../../../components/default-dialog';
import { useFeedback } from '../../../../hooks/use-feedback';
import { hasErrors } from '../../../../utils/validation';
import { parseNumberInput } from '../../../../utils/format';
import { getCategoryChanges, validatePublicationRequest } from '../components/publication-rules';
import { ACTION_TYPES, getTodayApiDate, useOfficialDiaryPublicationDispatch, useOfficialDiaryPublicationState } from "./official-diary-publication-context";

export const PUBLICATION_STEPS = ['Dados da publicação', 'Visualizar publicação'];
const FORM_STEP = 0;
const PREVIEW_STEP = 1;

export const useOfficialDiaryPublicationController = () => {
    const navigate = useNavigate();
    const { showDialog } = useBaseLayout();
    const { showWarning, showError, withLoading } = useFeedback();
    const dispatch = useOfficialDiaryPublicationDispatch();
    const state = useOfficialDiaryPublicationState();
    const { submissionPublicationId } = state.previewPublicationRequest;

    const validationErrors = validatePublicationRequest(state.previewPublicationRequest);
    const isLastStep = state.activeStep === PUBLICATION_STEPS.length - 1;

    useEffect(() => {
        loadInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function updateRequest(changes: Partial<IPreviewPublicationRequest>) {
        dispatch({ type: ACTION_TYPES.updatePreviewPublicationRequest, payload: changes });
    }

    async function loadInitialData() {
        await withLoading(async () => {
            try {
                const [categories, publicAgencies, documentTypes] = await Promise.all([
                    OfficialDiaryAppService.listCategories(),
                    OfficialDiaryAppService.listEntityPublication(),
                    BaseAppService.listDocumentTypes()
                ]);
                dispatch({ type: ACTION_TYPES.updateLookups, payload: { categories, publicAgencies, documentTypes } });

                // Publicação a partir de uma remessa: a entidade é a da remessa
                if (submissionPublicationId !== undefined) {
                    const submission = await SubmissionPublicationAppService.getById(submissionPublicationId);
                    updateRequest({ clientId: submission.clientId });
                }
            } catch (error: any) {
                showError('Não foi possível carregar os dados do formulário', error);
            }
        });
    }

    async function loadSubcategories(categoryId?: number) {
        dispatch({ type: ACTION_TYPES.updateLookups, payload: { subcategories: [] } });
        if (categoryId === undefined) return;

        await withLoading(async () => {
            try {
                const subcategories = await OfficialDiaryAppService.listSubcategories(categoryId);
                dispatch({ type: ACTION_TYPES.updateLookups, payload: { subcategories } });
            } catch (error: any) {
                showError('Não foi possível carregar as subcategorias', error);
            }
        });
    }

    function handleFieldChange(changes: Partial<IPreviewPublicationRequest>) {
        updateRequest(changes);
    }

    function handleCategoryChange(value: unknown) {
        const categoryId = parseNumberInput(value);
        updateRequest(getCategoryChanges(categoryId));
        loadSubcategories(categoryId);
    }

    function handleClientChange(e: any) {
        if (submissionPublicationId !== undefined) return;
        updateRequest({ clientId: parseNumberInput(e.target.value) });
    }

    function setFilesUpload(files: File[]) {
        dispatch({ type: ACTION_TYPES.setFiles, payload: files });
    }

    async function requestPreview() {
        await withLoading(async () => {
            try {
                const preview = await OfficialDiaryAppService.previewPublication(state.previewPublicationRequest, state.files);
                dispatch({ type: ACTION_TYPES.setPreviewPublicationResponse, payload: preview });
                dispatch({ type: ACTION_TYPES.setActiveStep, payload: PREVIEW_STEP });
            } catch (error: any) {
                showError('Não foi possível gerar a prévia da publicação', error);
            }
        });
    }

    function askForAnotherPublication() {
        showDialog({
            open: true,
            title: 'Publicação realizada',
            message: 'Publicação salva com sucesso. Deseja diagramar outra publicação?',
            type: 'question',
            onYesClick: () => dispatch({ type: ACTION_TYPES.resetForm, payload: { publicationDateOn: getTodayApiDate() } }),
            onNoClick: navigateBack,
        } as DefaultDialogProps);
    }

    async function handlePublish() {
        const request: IConfirmPublicationRequest = { ...state.previewPublicationResponse, submissionPublicationId };
        await withLoading(async () => {
            try {
                await OfficialDiaryAppService.confirmPublication(request);
                askForAnotherPublication();
            } catch (error: any) {
                showError('Não foi possível confirmar a publicação', error);
            }
        });
    }

    function handleNext() {
        dispatch({ type: ACTION_TYPES.setShowValidationErrors, payload: true });
        if (hasErrors(validationErrors)) {
            showWarning('É necessário preencher todos os campos obrigatórios.');
            return;
        }
        requestPreview();
    }

    function handleBack() {
        dispatch({ type: ACTION_TYPES.setActiveStep, payload: FORM_STEP });
    }

    // Volta para a origem: a listagem de remessas ou a do Diário Oficial
    function navigateBack() {
        navigate(submissionPublicationId !== undefined ? '/remessas-publicacao' : '/diario-oficial');
    }

    return {
        isLastStep,
        validationErrors: state.showValidationErrors ? validationErrors : {},
        handleFieldChange,
        handleCategoryChange,
        handleClientChange,
        setFilesUpload,
        handleNext,
        handleBack,
        handlePublish,
        navigateBack
    };
}
